import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { HandleChip } from '@/components/users/handle-chip'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { FeedEntry } from '@/api/types'

interface FeedEntryCardProps {
  entry: FeedEntry
}

const formatRelative = (iso: string): string => {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 14) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Phase 14 — one row in the dashboard Subscriptions feed. Click anywhere navigates to the
 * project's canonical URL.
 */
export const FeedEntryCard = ({ entry }: FeedEntryCardProps) => {
  const href = canonicalProjectUrl(entry.author.handle, entry.project.slug)
  return (
    <Link to={href} className="block">
      <Card className="transition-colors hover:bg-accent/40">
        <CardContent className="flex items-start gap-3 py-3">
          {entry.project.heroThumbUrl ? (
            <img
              src={entry.project.heroThumbUrl}
              alt=""
              className="h-16 w-24 shrink-0 rounded object-cover"
            />
          ) : (
            <div className="h-16 w-24 shrink-0 rounded bg-muted" aria-hidden />
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-baseline gap-2 text-xs text-muted-foreground">
              <HandleChip author={entry.author} />
              <span>published</span>
              <span>·</span>
              <span>{formatRelative(entry.publishedAt)}</span>
              <span className="rounded-full border border-border bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                v{entry.version}
              </span>
            </div>
            <h3 className="truncate text-base font-medium">{entry.project.title}</h3>
            {entry.project.descriptionExcerpt && (
              <p className="truncate text-sm text-muted-foreground">
                {entry.project.descriptionExcerpt}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}
